// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "@github/clipboard-copy-element"
import "@github/relative-time-element"
import './autocomplete-element'
import './performer-links-element'
import './sortable-list-element'
import './checkbox-auto-submit-element'
import './passkey-registration-element'
import './passkey-verification-element'
import './sales-chart-element'
import './live-chat-element'
import './chat-replay-element'
import './chat-replay-calibrator-element'
import './stream-manager-element'
import './masked-value-element'
import './qr-ticket-scanner-element'
import './modal-dialog-element'
import './announcement-composer-element'
import { install } from '@github/hotkey'

function installHotkeys() {
  for (const el of document.querySelectorAll('[data-hotkey]')) {
    install(el as HTMLElement)
  }
}

// Instant hover tooltips for [data-tooltip] elements. Rendered fixed-position
// on body — like the "Copied!" bubble below — so overflow/scroll containers
// (e.g. the live chat message list) can't clip them.
let hoverTooltip: HTMLElement | null = null
let hoverTooltipAnchor: Element | null = null

function hideTooltip() {
  hoverTooltip?.remove()
  hoverTooltip = null
  hoverTooltipAnchor = null
}

function showTooltip(anchor: HTMLElement) {
  if (anchor === hoverTooltipAnchor) return
  hideTooltip()

  const text = anchor.getAttribute("data-tooltip")
  if (!text) return

  const rect = anchor.getBoundingClientRect()
  const tooltip = document.createElement("div")
  tooltip.textContent = text
  tooltip.className = "fixed -translate-x-1/2 -translate-y-full px-2 py-1 text-xs text-white bg-zinc-700 rounded shadow-lg whitespace-nowrap z-50 pointer-events-none"
  tooltip.style.left = `${rect.left + rect.width / 2}px`
  tooltip.style.top = `${rect.top - 4}px`
  document.body.appendChild(tooltip)

  // Keep it inside the viewport when the anchor sits near an edge
  const tooltipRect = tooltip.getBoundingClientRect()
  if (tooltipRect.right > window.innerWidth - 4) {
    tooltip.style.left = `${rect.left + rect.width / 2 - (tooltipRect.right - (window.innerWidth - 4))}px`
  } else if (tooltipRect.left < 4) {
    tooltip.style.left = `${rect.left + rect.width / 2 + (4 - tooltipRect.left)}px`
  }

  hoverTooltip = tooltip
  hoverTooltipAnchor = anchor
}

function installTooltips() {
  document.addEventListener("pointerover", (event) => {
    const anchor = (event.target as HTMLElement).closest?.("[data-tooltip]")
    if (anchor) {
      showTooltip(anchor as HTMLElement)
    } else if (hoverTooltip) {
      hideTooltip()
    }
  })
  document.addEventListener("focusin", (event) => {
    const anchor = (event.target as HTMLElement).closest?.("[data-tooltip]")
    if (anchor) showTooltip(anchor as HTMLElement)
  })
  document.addEventListener("focusout", hideTooltip)
  // Positions go stale on scroll; clicking means the action was taken
  document.addEventListener("scroll", hideTooltip, true)
  document.addEventListener("click", hideTooltip)
}

document.addEventListener("DOMContentLoaded", () => {
  installHotkeys()
  installTooltips()

  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement

    // Close dropdowns when clicking outside
    document.querySelectorAll("details[data-dropdown][open]").forEach((dropdown) => {
      if (!dropdown.contains(target)) {
        dropdown.removeAttribute("open")
      }
    })

    // Close dropdown when clicking an item inside it (not the summary toggle)
    const item = target.closest("details[data-dropdown] > div button, details[data-dropdown] > div a")
    if (item) {
      item.closest("details[data-dropdown]")?.removeAttribute("open")
    }
  })

  document.addEventListener("clipboard-copy", (event) => {
    const clipboardCopy = event.target as HTMLElement

    // Fixed-position on body so overflow/scroll containers can't clip it
    const rect = clipboardCopy.getBoundingClientRect()
    const tooltip = document.createElement("div")
    tooltip.textContent = "Copied!"
    tooltip.className = "fixed -translate-x-1/2 px-2 py-1 text-xs text-white bg-zinc-700 rounded shadow-lg whitespace-nowrap z-50"
    tooltip.style.left = `${rect.left + rect.width / 2}px`
    tooltip.style.top = `${rect.top - 32}px`
    document.body.appendChild(tooltip)

    // Remove tooltip after delay
    setTimeout(() => {
      tooltip.remove()
    }, 1500)
  })
})

document.addEventListener("turbo:load", installHotkeys)


